import { createContext, useCallback, useEffect, useMemo, useState } from "react";
import { Attribute, AttributeGroup, ComponentProps, ConfigsProvider } from "react-builder";
import { useProductApi } from "../../custom-hooks/useProductApi";
import { Game, Movie, MultimediaUserData, Series } from "../../types/multimedia-types";

@AttributeGroup({
  title: "MultimediaUserDataProvider",
  blockName: "MultimediaUserDataProvider",
  module: "multimedia",
  icon: "query",
})
export class MultimediaUserDataProviderAttributes extends Attribute {}

interface MultimediaMultimediaUserDataContextType {
  favoritesGames: Game[] | undefined;
  favoritesMovies: Movie[] | undefined;
  favoritesSeries: Series[] | undefined;
  addMovieToFavorites: (movie: Movie) => void;
  addSeriesToFavorites: (series: Series) => void;
  addGameToFavorites: (game: Game) => void;
  removeMovieFromFavorites: (movieId: string) => void;
  removeSeriesFromFavorites: (seriesId: string) => void;
  removeGameFromFavorites: (gameId: string) => void;
}

export const MultimediaUserDataContext = createContext<MultimediaMultimediaUserDataContextType>(
  {} as any
);

export function MultimediaUserDataProvider(
  props: ComponentProps<MultimediaUserDataProviderAttributes>
) {
  const multimediaUserDataAPI = useProductApi<MultimediaUserData>();
  const [multimediaUserData, setMultimediaUserData] = useState<MultimediaUserData>();

  const list = useCallback(async () => {
    if (!multimediaUserDataAPI) return;
    try {
      const multimediaUserDataPromise = await multimediaUserDataAPI.single(
        "multimedia/ProductUserData",
        {
          populate: `[{"path": "favoritesMovies", "populate": [{"path": "preview"}]},
            {"path": "favoritesSeries", "populate": [{"path": "preview"}]},
            {"path": "favoritesGames", "populate": [{"path": "image"}]}]`,
        }
      );
      setMultimediaUserData(multimediaUserDataPromise);
    } catch (error) {
      console.error("Error fetching MultimediaUserData:", error);
    }
  }, [multimediaUserDataAPI]);

  useEffect(() => {
    list();
  }, [multimediaUserDataAPI]);

  const addToFavorites = async (item: Movie | Series | Game, medium: string) => {
    try {
      if (!multimediaUserDataAPI || !item || !medium) return;
      // immediate change for good ux
      setMultimediaUserData((prev) => {
        if (!prev) return prev;
        switch (medium) {
          case "Movie": {
            return {
              ...prev,
              favoritesMovies: [...prev.favoritesMovies, item as Movie],
            };
          }
          case "Series": {
            return {
              ...prev,
              favoritesSeries: [...prev.favoritesSeries, item as Series],
            };
          }
          case "Game": {
            return {
              ...prev,
              favoritesGames: [...prev.favoritesGames, item as Game],
            };
          }
          default:
            return prev;
        }
      });
      // post change to backend
      await multimediaUserDataAPI.post(`multimedia/${medium}/${item._id}/like`);
      // fetch again for populated result
      // list();
    } catch (error) {
      console.log("error: ", error);
    }
  };

  const removeFromFavorites = async (itemId: string, medium: string) => {
    try {
      if (!multimediaUserDataAPI || !itemId || !medium) return;
      setMultimediaUserData((prev) => {
        if (!prev) return prev;
        switch (medium) {
          case "Movie": {
            return {
              ...prev,
              favoritesMovies: prev.favoritesMovies.filter((movie) => movie._id !== itemId),
            };
          }
          case "Series": {
            return {
              ...prev,
              favoritesSeries: prev.favoritesSeries.filter((series) => series._id !== itemId),
            };
          }
          case "Game": {
            return {
              ...prev,
              favoritesGames: prev.favoritesGames.filter((game) => game._id !== itemId),
            };
          }
          default:
            return prev;
        }
      });
      await multimediaUserDataAPI.post(`multimedia/${medium}/${itemId}/unlike`);
    } catch (error) {
      console.log("error: ", error);
    }
  };

  const isFavorite = (itemId: string, medium: string) => {
    switch (medium) {
      case "Movie": {
        if (!multimediaUserData?.favoritesMovies || !multimediaUserData?.favoritesMovies.length)
          return false;
        return !!multimediaUserData.favoritesMovies?.find((m) => m._id === itemId);
      }
      case "Series": {
        if (!multimediaUserData?.favoritesSeries || !multimediaUserData?.favoritesSeries.length)
          return false;
        return !!multimediaUserData.favoritesSeries?.find((s) => s._id === itemId);
      }
      case "Game": {
        if (!multimediaUserData?.favoritesGames || !multimediaUserData?.favoritesGames.length)
          return false;
        return !!multimediaUserData.favoritesGames?.find((g) => g._id === itemId);
      }
    }
  };

  const data = {
    favoritesGames: multimediaUserData?.favoritesGames,
    favoritesMovies: multimediaUserData?.favoritesMovies,
    favoritesSeries: multimediaUserData?.favoritesSeries,
    addMovieToFavorites: (item: Movie) => {
      addToFavorites(item, "Movie");
    },
    addSeriesToFavorites: (item: Series) => {
      addToFavorites(item, "Series");
    },
    addGameToFavorites: (item: Game) => {
      addToFavorites(item, "Game");
    },
    removeMovieFromFavorites: (itemId: string) => {
      removeFromFavorites(itemId, "Movie");
    },
    removeSeriesFromFavorites: (itemId: string) => {
      removeFromFavorites(itemId, "Series");
    },
    removeGameFromFavorites: (itemId: string) => {
      removeFromFavorites(itemId, "Game");
    },
  };
  return (
    <MultimediaUserDataContext.Provider value={data}>
      <ConfigsProvider
        configJson={{
          multimediaUserData: {
            ...data,
            favoritesGames: data.favoritesGames?.map((i) => {
              return {
                ...i,
                isFavorite: isFavorite(i._id, "Game"),
                addGameToFavorites: () => data.addGameToFavorites(i),
                removeGameFromFavorites: () => data.removeGameFromFavorites(i._id),
              };
            }),
            favoritesMovies: data.favoritesMovies?.map((i) => {
              return {
                ...i,
                isFavorite: isFavorite(i._id, "Movie"),
                addMovieToFavorites: () => data.addMovieToFavorites(i),
                removeMovieFromFavorites: () => data.removeMovieFromFavorites(i._id),
              };
            }),
            favoritesSeries: data.favoritesSeries?.map((i) => {
              return {
                ...i,
                isFavorite: isFavorite(i._id, "Series"),
                addSeriesToFavorites: () => data.addSeriesToFavorites(i),
                removeSeriesFromFavorites: () => data.removeSeriesFromFavorites(i._id),
              };
            }),
          },
        }}
      >
        <>{props.children}</>
      </ConfigsProvider>
    </MultimediaUserDataContext.Provider>
  );
}

MultimediaUserDataProvider.prototype.Props = MultimediaUserDataProviderAttributes;

export default MultimediaUserDataProvider;
